import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CourseQuestion {
  id: string;
  course_id: string;
  module_id: string | null;
  user_id: string;
  title: string;
  content: string;
  is_resolved: boolean | null;
  created_at: string;
  updated_at: string;
  author?: {
    full_name: string;
    avatar_url: string | null;
  };
  answer_count?: number;
}

export interface QuestionAnswer {
  id: string;
  question_id: string;
  user_id: string;
  content: string;
  is_accepted: boolean | null;
  created_at: string;
  updated_at: string;
  author?: {
    full_name: string;
    avatar_url: string | null;
  };
}

export function useCourseQuestions(courseId: string) {
  return useQuery({
    queryKey: ["course-questions", courseId],
    queryFn: async () => {
      const { data: questions, error } = await supabase
        .from("course_questions")
        .select("*")
        .eq("course_id", courseId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const userIds = [...new Set(questions.map((q) => q.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", userIds);

      const questionIds = questions.map((q) => q.id);
      const { data: answerCounts } = await supabase
        .from("question_answers")
        .select("question_id")
        .in("question_id", questionIds);

      const answerCountMap: Record<string, number> = {};
      answerCounts?.forEach((a) => {
        answerCountMap[a.question_id] = (answerCountMap[a.question_id] || 0) + 1;
      });

      return questions.map((question) => ({
        ...question,
        author: profiles?.find((p) => p.id === question.user_id),
        answer_count: answerCountMap[question.id] || 0,
      })) as CourseQuestion[];
    },
    enabled: !!courseId,
  });
}

export function useCourseQuestion(questionId: string) {
  return useQuery({
    queryKey: ["course-question", questionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("course_questions")
        .select("*")
        .eq("id", questionId)
        .single();

      if (error) throw error;

      const { data: profile } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .eq("id", data.user_id)
        .single();

      return {
        ...data,
        author: profile,
      } as CourseQuestion;
    },
    enabled: !!questionId,
  });
}

export function useQuestionAnswers(questionId: string) {
  return useQuery({
    queryKey: ["question-answers", questionId],
    queryFn: async () => {
      const { data: answers, error } = await supabase
        .from("question_answers")
        .select("*")
        .eq("question_id", questionId)
        .order("is_accepted", { ascending: false })
        .order("created_at", { ascending: true });

      if (error) throw error;

      const userIds = [...new Set(answers.map((a) => a.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", userIds);

      return answers.map((answer) => ({
        ...answer,
        author: profiles?.find((p) => p.id === answer.user_id),
      })) as QuestionAnswer[];
    },
    enabled: !!questionId,
  });
}

export function useCreateQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      courseId,
      moduleId,
      userId,
      title,
      content,
    }: {
      courseId: string;
      moduleId?: string;
      userId: string;
      title: string;
      content: string;
    }) => {
      const { data, error } = await supabase
        .from("course_questions")
        .insert({
          course_id: courseId,
          module_id: moduleId || null,
          user_id: userId,
          title,
          content,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["course-questions", variables.courseId],
      });
    },
  });
}

export function useCreateAnswer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      questionId,
      userId,
      content,
    }: {
      questionId: string;
      userId: string;
      content: string;
    }) => {
      const { data, error } = await supabase
        .from("question_answers")
        .insert({
          question_id: questionId,
          user_id: userId,
          content,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["question-answers", variables.questionId],
      });
      queryClient.invalidateQueries({ queryKey: ["course-questions"] });
    },
  });
}

export function useToggleQuestionResolved() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      questionId,
      isResolved,
    }: {
      questionId: string;
      isResolved: boolean;
    }) => {
      const { error } = await supabase
        .from("course_questions")
        .update({ is_resolved: isResolved })
        .eq("id", questionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course-questions"] });
      queryClient.invalidateQueries({ queryKey: ["course-question"] });
    },
  });
}

export function useToggleAnswerAccepted() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      answerId,
      isAccepted,
    }: {
      answerId: string;
      isAccepted: boolean;
    }) => {
      const { error } = await supabase
        .from("question_answers")
        .update({ is_accepted: isAccepted })
        .eq("id", answerId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["question-answers"] });
    },
  });
}

export function useDeleteQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (questionId: string) => {
      const { error } = await supabase
        .from("course_questions")
        .delete()
        .eq("id", questionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course-questions"] });
    },
  });
}

export function useDeleteAnswer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (answerId: string) => {
      const { error } = await supabase
        .from("question_answers")
        .delete()
        .eq("id", answerId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["question-answers"] });
      queryClient.invalidateQueries({ queryKey: ["course-questions"] });
    },
  });
}
